/**
 * ConfigPage
 * Gestión de configuración del sistema y políticas
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/api/client';
import { PWAStatus } from '@/components/PWAStatus';
import { useThemeModeContext } from '@/contexts/ThemeModeContext';
import type { AgentPolicy } from '@/types/config';
import PageHeader from '@/components/common/PageHeader';

type ConfigSection = 'app' | 'system' | 'policies' | 'security';

const sectionLabels: Record<ConfigSection, string> = {
  app: 'App',
  system: 'Sistema',
  policies: 'Políticas',
  security: 'Seguridad',
};

export const ConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeSection, setActiveSection] = useState<ConfigSection>('app');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // App-level parameters (persisted to localStorage)
  const { themeMode: currentThemeMode, setThemeMode } = useThemeModeContext();
  const [appSettings, setAppSettings] = useState({
    enableTelemetry: false,
    sessionTimeout: 30,
    themeMode: currentThemeMode as any,
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem('appSettings');
      if (raw) {
        const parsed = JSON.parse(raw);
        setAppSettings((s) => ({ ...s, ...parsed }));
      }
    } catch (_e) {
      // ignore
    }
  }, []);

  const handleSaveAppSettings = () => {
    try {
      // apply theme immediately
      if (appSettings.themeMode) setThemeMode(appSettings.themeMode);
      localStorage.setItem('appSettings', JSON.stringify(appSettings));
      setSuccess('Parámetros de la aplicación guardados');
      setTimeout(() => setSuccess(null), 2500);
    } catch (_e) {
      setError('Error guardando parámetros de la app');
    }
  };

  // Dialog for agent policy management
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [policyFormData, setPolicyFormData] = useState<Partial<AgentPolicy>>({
    allowDangerous: false,
    allowRemoteControl: true,
    allowShutdown: false,
    allowReboot: false,
    maxConcurrentSessions: 1,
  });

  const handleOpenPolicyDialog = () => {
    setSelectedAgentId('');
    setPolicyFormData({
      allowDangerous: false,
      allowRemoteControl: true,
      allowShutdown: false,
      allowReboot: false,
      maxConcurrentSessions: 1,
    });
    setPolicyDialogOpen(true);
  };

  const handleClosePolicyDialog = () => {
    setPolicyDialogOpen(false);
  };

  const handleSavePolicy = async () => {
    if (!selectedAgentId) {
      setError('Debe especificar un ID de agente');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await apiClient.put(`/admin/agents/${selectedAgentId}/policy`, {
        ...policyFormData,
      });
      setSuccess('Política actualizada correctamente');
      handleClosePolicyDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar la política');
    } finally {
      setLoading(false);
    }
  };

  return (
    // Fill the remaining viewport (set up by MainLayout's flex column <main>)
    // and let the content below scroll internally — body never scrolls.
    // `flex: 1` + `minHeight: 0` lets this Box shrink below its content size;
    // without `minHeight: 0` flex items default to `min-height: auto` and
    // ignore `overflow: auto` on their children.
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Pinned region: header, alerts, mobile tabs — never scrolls */}
      <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PageHeader
          icon={<SettingsIcon />}
          title="Configuración"
          subtitle="Gestión de políticas de seguridad y configuración del sistema."
          actions={
            <Button variant="outlined" onClick={() => navigate('/agents')}>
              Volver
            </Button>
          }
        />

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        {/* Mobile-only tabs to navigate between the 4 sections; on desktop we
            keep them stacked to preserve the classic "one scroll" feel. */}
        {isMobile && (
          <Tabs
            value={activeSection}
            onChange={(_, v) => setActiveSection(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {(Object.keys(sectionLabels) as ConfigSection[]).map((k) => (
              <Tab key={k} value={k} label={sectionLabels[k]} />
            ))}
          </Tabs>
        )}
      </Box>

      {/* Scrollable region — only the content area scrolls, body stays put.
          `gap: 2` keeps air between the section cards. `pr` keeps the
          scrollbar from overlapping the cards' right edge. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflowY: 'auto',
          pr: 1,
        }}
      >
        {/* App Parameters Section */}
        <Box sx={{ display: { xs: activeSection === 'app' ? 'block' : 'none', md: 'block' } }}>
          {/* PWA Status lives inside App on mobile, stacked above on desktop. */}
          {isMobile && <PWAStatus />}

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Parámetros de la App
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Ajustes globales de la aplicación (persistidos en el navegador).
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={!!appSettings.enableTelemetry}
                    onChange={(e) =>
                      setAppSettings({ ...appSettings, enableTelemetry: e.target.checked })
                    }
                  />
                }
                label="Habilitar Telemetría (anónima)"
              />

              <TextField
                label="Timeout de sesión (minutos)"
                type="number"
                size="small"
                value={appSettings.sessionTimeout}
                onChange={(e) =>
                  setAppSettings({
                    ...appSettings,
                    sessionTimeout: parseInt(e.target.value || '0') || 0,
                  })
                }
                inputProps={{ min: 1, max: 1440 }}
                sx={{ maxWidth: 220 }}
              />

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2" sx={{ width: '100%' }}>
                  Tema:
                </Typography>
                <Button
                  variant={appSettings.themeMode === 'light' ? 'contained' : 'outlined'}
                  onClick={() => setAppSettings({ ...appSettings, themeMode: 'light' as any })}
                >
                  Claro
                </Button>
                <Button
                  variant={appSettings.themeMode === 'dark' ? 'contained' : 'outlined'}
                  onClick={() => setAppSettings({ ...appSettings, themeMode: 'dark' as any })}
                >
                  Oscuro
                </Button>
                <Button
                  variant={appSettings.themeMode === 'auto' ? 'contained' : 'outlined'}
                  onClick={() => setAppSettings({ ...appSettings, themeMode: 'auto' as any })}
                >
                  Auto
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button variant="contained" onClick={handleSaveAppSettings}>
                  Guardar parámetros
                </Button>
                <Button
                  onClick={() => {
                    try {
                      localStorage.removeItem('appSettings');
                      setAppSettings({
                        enableTelemetry: false,
                        sessionTimeout: 30,
                        themeMode: currentThemeMode as any,
                      });
                    } catch (_e) {
                      /* ignore */
                    }
                  }}
                >
                  Restaurar valores
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>

        {/* System Settings Section */}
        <Box sx={{ display: { xs: activeSection === 'system' ? 'block' : 'none', md: 'block' } }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Configuración del Sistema
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Versión de la aplicación y estado del sistema.
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">API Base URL</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={import.meta.env.VITE_API_URL || '/api/v1'}
                    InputProps={{ readOnly: true }}
                  />
                </Box>
              </Stack>
            </Stack>
          </Paper>
        </Box>

        {/* Agent Policies Section */}
        <Box sx={{ display: { xs: activeSection === 'policies' ? 'block' : 'none', md: 'block' } }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Políticas de Agentes
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Configura controles de seguridad por agente.
              </Typography>
              <Button variant="contained" onClick={handleOpenPolicyDialog}>
                Configurar Política de Agente
              </Button>
            </Stack>
          </Paper>
        </Box>

        {/* Security Settings Section */}
        <Box sx={{ display: { xs: activeSection === 'security' ? 'block' : 'none', md: 'block' } }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Opciones de Seguridad
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <FormControlLabel
                control={<Switch defaultChecked={false} disabled />}
                label="Modo Mantenimiento (requiere reinicio del servidor)"
              />
              <FormControlLabel
                control={<Switch defaultChecked={false} disabled />}
                label="Modo Debug (aumenta verbosidad)"
              />
              <Typography variant="caption" color="text.secondary">
                Estos parámetros deben configurarse desde variables de entorno.
              </Typography>
            </Stack>
          </Paper>
        </Box>

        {/* PWA Status lives outside the tabs on desktop. */}
        {!isMobile && <PWAStatus />}
      </Box>

      {/* Policy Dialog */}
      <Dialog open={policyDialogOpen} onClose={handleClosePolicyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Configurar Política de Agente</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="ID del Agente"
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              placeholder="agent-123"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={policyFormData.allowDangerous ?? false}
                  onChange={(e) =>
                    setPolicyFormData({ ...policyFormData, allowDangerous: e.target.checked })
                  }
                />
              }
              label="Permitir operaciones peligrosas (apagar, reiniciar)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={policyFormData.allowRemoteControl ?? true}
                  onChange={(e) =>
                    setPolicyFormData({ ...policyFormData, allowRemoteControl: e.target.checked })
                  }
                />
              }
              label="Permitir control remoto"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={policyFormData.allowShutdown ?? false}
                  onChange={(e) =>
                    setPolicyFormData({ ...policyFormData, allowShutdown: e.target.checked })
                  }
                />
              }
              label="Permitir apagar el agente"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={policyFormData.allowReboot ?? false}
                  onChange={(e) =>
                    setPolicyFormData({ ...policyFormData, allowReboot: e.target.checked })
                  }
                />
              }
              label="Permitir reiniciar el agente"
            />
            <TextField
              fullWidth
              type="number"
              label="Máximo de sesiones concurrentes"
              value={policyFormData.maxConcurrentSessions ?? 1}
              onChange={(e) =>
                setPolicyFormData({
                  ...policyFormData,
                  maxConcurrentSessions: parseInt(e.target.value) || 1,
                })
              }
              inputProps={{ min: 1, max: 10 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePolicyDialog}>Cancelar</Button>
          <Button
            onClick={handleSavePolicy}
            variant="contained"
            disabled={loading || !selectedAgentId}
          >
            {loading ? <CircularProgress size={20} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
